import React, { useState, useRef, useEffect } from 'react';
import { ProjectTemplate, ProjectParameters, SavedChiffrage } from '../../types';
import { PARAMETER_DEFINITIONS } from '../../types/ProjectParameters';
import ParamChip from './ParamChip';
import AddParamButton from './AddParamButton';

// ── Gabarit dropdown ──────────────────────────────────────────────────────────

interface GabaritDropdownProps {
  templates: ProjectTemplate[];
  onLoad: (template: ProjectTemplate) => void;
  onSave?: (name: string) => void;
  savedChiffrages?: SavedChiffrage[];
  onDuplicateFrom?: (chiffrage: SavedChiffrage) => void;
  onSaveChiffrage?: (name: string) => void;
}

const GabaritDropdown: React.FC<GabaritDropdownProps> = ({
  templates, onLoad, onSave,
  savedChiffrages = [], onDuplicateFrom, onSaveChiffrage,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [savingChiffrage, setSavingChiffrage] = useState(false);
  const [saveChiffrageName, setSaveChiffrageName] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const saveRef = useRef<HTMLInputElement>(null);
  const saveChiffrageRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
        setSaving(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  // Focus search quand le dropdown s'ouvre
  useEffect(() => {
    if (isOpen) setTimeout(() => searchRef.current?.focus(), 0);
  }, [isOpen]);

  // Focus input save quand on bascule en mode sauvegarde
  useEffect(() => {
    if (saving) setTimeout(() => saveRef.current?.focus(), 0);
  }, [saving]);

  useEffect(() => {
    if (savingChiffrage) setTimeout(() => saveChiffrageRef.current?.focus(), 0);
  }, [savingChiffrage]);

  const filtered = query.trim()
    ? templates.filter(t =>
        t.nom.toLowerCase().includes(query.toLowerCase()) ||
        t.description.toLowerCase().includes(query.toLowerCase())
      )
    : templates;

  const handleSave = () => {
    if (!saveName.trim() && !onSave) return;
    onSave?.(saveName.trim());
    setSaving(false);
    setSaveName('');
    setIsOpen(false);
  };

  const handleSaveChiffrage = () => {
    if (!saveChiffrageName.trim()) return;
    onSaveChiffrage?.(saveChiffrageName.trim());
    setSavingChiffrage(false);
    setSaveChiffrageName('');
    setIsOpen(false);
  };

  const fmtDate = (d: Date) =>
    new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' }).format(d);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setIsOpen(o => !o)}
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-violet-400 hover:text-violet-700 transition-colors"
      >
        Gabarit
        <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64 flex flex-col">
          {/* Barre de recherche */}
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-50 rounded-md">
              <svg className="w-3 h-3 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Rechercher un gabarit…"
                className="flex-1 text-xs bg-transparent outline-none text-gray-700 placeholder-gray-400"
                onKeyDown={e => e.key === 'Escape' && setIsOpen(false)}
              />
              {query && (
                <button onClick={() => setQuery('')} className="text-gray-300 hover:text-gray-500">
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Liste des gabarits */}
          <div className="overflow-y-auto max-h-48 py-1">
            {filtered.length > 0 ? filtered.map(t => (
              <button
                key={t.id}
                onClick={() => { onLoad(t); setIsOpen(false); setQuery(''); }}
                className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-violet-50 hover:text-violet-700"
              >
                <div className="font-medium">{t.nom}</div>
                {t.description && <div className="text-gray-400 mt-0.5">{t.description}</div>}
              </button>
            )) : (
              <p className="px-3 py-3 text-xs text-gray-400 text-center italic">Aucun gabarit trouvé</p>
            )}
          </div>

          {/* Section "Dupliquer depuis" */}
          {(savedChiffrages.length > 0 || onSaveChiffrage) && (
            <div className="border-t border-gray-100">
              <div className="px-3 pt-2 pb-1">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Dupliquer depuis</span>
              </div>
              {savedChiffrages.length > 0 ? (
                <div className="overflow-y-auto max-h-36">
                  {savedChiffrages.map(c => (
                    <button
                      key={c.id}
                      onClick={() => { onDuplicateFrom?.(c); setIsOpen(false); }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-violet-50 hover:text-violet-700 flex items-center justify-between gap-2"
                    >
                      <span className="font-medium truncate">{c.nom}</span>
                      <span className="text-gray-400 flex-shrink-0 tabular-nums">{fmtDate(c.savedAt)} · {c.blocks.length} lignes</span>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="px-3 pb-2 text-xs text-gray-300 italic">Aucun chiffrage sauvegardé</p>
              )}

              {/* Sauvegarder ce chiffrage */}
              {onSaveChiffrage && (
                <div className="px-2 pb-2">
                  {savingChiffrage ? (
                    <div className="flex items-center gap-1.5">
                      <input
                        ref={saveChiffrageRef}
                        type="text"
                        value={saveChiffrageName}
                        onChange={e => setSaveChiffrageName(e.target.value)}
                        placeholder="Nom du chiffrage…"
                        className="flex-1 text-xs px-2 py-1 border border-violet-300 rounded-md outline-none focus:ring-1 focus:ring-violet-400 bg-violet-50"
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveChiffrage();
                          else if (e.key === 'Escape') { setSavingChiffrage(false); setSaveChiffrageName(''); }
                        }}
                      />
                      <button
                        onClick={handleSaveChiffrage}
                        disabled={!saveChiffrageName.trim()}
                        className="px-2 py-1 text-xs bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        OK
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setSavingChiffrage(true); setSaveChiffrageName(''); }}
                      className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 hover:text-violet-700 hover:bg-violet-50 rounded-md transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                      </svg>
                      Sauvegarder ce chiffrage
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Sauvegarder comme gabarit */}
          {onSave && (
            <div className="border-t border-gray-100 p-2">
              {saving ? (
                <div className="flex items-center gap-1.5">
                  <input
                    ref={saveRef}
                    type="text"
                    value={saveName}
                    onChange={e => setSaveName(e.target.value)}
                    placeholder="Nom du gabarit…"
                    className="flex-1 text-xs px-2 py-1 border border-violet-300 rounded-md outline-none focus:ring-1 focus:ring-violet-400 bg-violet-50"
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleSave();
                      else if (e.key === 'Escape') { setSaving(false); setSaveName(''); }
                    }}
                  />
                  <button
                    onClick={handleSave}
                    disabled={!saveName.trim()}
                    className="px-2 py-1 text-xs bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    OK
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => { setSaving(true); setSaveName(''); }}
                  className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-gray-500 hover:text-violet-700 hover:bg-violet-50 rounded-md transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Sauvegarder comme gabarit
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── ProjectHeader ─────────────────────────────────────────────────────────────

interface ProjectHeaderProps {
  nomProjet: string;
  onNomProjetChange: (nom: string) => void;
  onExportExcel: () => void;
  onLoadTemplate?: (template: ProjectTemplate) => void;
  onSaveTemplate?: (name: string) => void;
  templates?: ProjectTemplate[];
  savedChiffrages?: SavedChiffrage[];
  onDuplicateFrom?: (chiffrage: SavedChiffrage) => void;
  onSaveChiffrage?: (name: string) => void;
  projectParameters?: ProjectParameters;
  onProjectParametersChange?: (parameters: ProjectParameters) => void;
  visibleParams: Set<keyof ProjectParameters>;
  onVisibleParamsChange: (params: Set<keyof ProjectParameters>) => void;
  activeParams: Set<keyof ProjectParameters>;
  onActiveParamsChange: (params: Set<keyof ProjectParameters>) => void;
}

export const ProjectHeader: React.FC<ProjectHeaderProps> = ({
  nomProjet,
  onNomProjetChange,
  onExportExcel,
  onLoadTemplate,
  onSaveTemplate,
  templates = [],
  savedChiffrages = [],
  onDuplicateFrom,
  onSaveChiffrage,
  projectParameters,
  onProjectParametersChange,
  visibleParams,
  onVisibleParamsChange,
  activeParams,
  onActiveParamsChange,
}) => {
  const [isEditingNom, setIsEditingNom] = useState(false);
  const [editingNom, setEditingNom] = useState('');

  const handleNomProjetChange = (newNom: string) => {
    const finalNom = newNom.trim() === '' ? 'Mon Super Projet' : newNom.trim();
    onNomProjetChange(finalNom);
    setIsEditingNom(false);
  };

  const handleStartEditing = () => {
    setEditingNom(nomProjet === 'Mon Super Projet' ? '' : nomProjet);
    setIsEditingNom(true);
  };

  const handleFinishEditing = () => {
    handleNomProjetChange(editingNom);
  };

  const handleAddParam = (key: keyof ProjectParameters) => {
    onVisibleParamsChange(new Set([...Array.from(visibleParams), key]));
    onActiveParamsChange(new Set([...Array.from(activeParams), key]));
  };

  const handleRemoveParam = (key: keyof ProjectParameters) => {
    const next = new Set(visibleParams);
    next.delete(key);
    onVisibleParamsChange(next);
    const nextActive = new Set(activeParams);
    nextActive.delete(key);
    onActiveParamsChange(nextActive);
  };

  const handleToggleActive = (key: keyof ProjectParameters) => {
    const next = new Set(activeParams);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onActiveParamsChange(next);
  };

  const handleParamChange = (key: keyof ProjectParameters, value: string) => {
    if (projectParameters && onProjectParametersChange) {
      onProjectParametersChange({ ...projectParameters, [key]: value });
    }
  };

  return (
    <div className="mb-4">
      {/* Row 1 : nom + gabarit + export */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex-1 min-w-0">
          {isEditingNom ? (
            <input
              type="text"
              value={editingNom}
              onChange={(e) => setEditingNom(e.target.value)}
              onBlur={handleFinishEditing}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); handleFinishEditing(); }
                else if (e.key === 'Escape') { e.preventDefault(); setIsEditingNom(false); }
              }}
              className="w-full text-xl font-semibold text-gray-900 border-none outline-none focus:ring-2 focus:ring-violet-500 rounded px-2 py-1 bg-gray-50"
              autoFocus
              placeholder="Nom du projet"
            />
          ) : (
            <div
              className="group flex items-center space-x-2 cursor-pointer hover:bg-gray-50 rounded-lg px-3 py-2 -mx-3 -my-2 transition-all duration-200"
              onClick={handleStartEditing}
              title="Cliquez pour renommer le projet"
            >
              <h2 className={`text-xl font-semibold truncate ${nomProjet === 'Mon Super Projet' ? 'text-gray-400 italic' : 'text-gray-900'}`}>
                {nomProjet}
              </h2>
              <svg className="w-4 h-4 text-gray-400 group-hover:text-violet-600 transition-colors opacity-60 group-hover:opacity-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {onLoadTemplate && (
            <GabaritDropdown
              templates={templates}
              onLoad={onLoadTemplate}
              onSave={onSaveTemplate}
              savedChiffrages={savedChiffrages}
              onDuplicateFrom={onDuplicateFrom}
              onSaveChiffrage={onSaveChiffrage}
            />
          )}
          <button
            onClick={onExportExcel}
            className="flex items-center space-x-1 px-3 py-1.5 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Row 2 : chips de paramètres */}
      {projectParameters && (
        <div className="flex items-center gap-1.5 flex-wrap min-h-[24px]">
          {Array.from(visibleParams).map(key => {
            const def = PARAMETER_DEFINITIONS.find(d => d.key === key);
            if (!def) return null;
            return (
              <ParamChip
                key={key}
                def={def}
                value={projectParameters[key]}
                isActive={activeParams.has(key)}
                onChange={handleParamChange}
                onRemove={handleRemoveParam}
                onToggleActive={handleToggleActive}
              />
            );
          })}
          <AddParamButton
            definitions={PARAMETER_DEFINITIONS}
            visibleKeys={visibleParams}
            onAdd={handleAddParam}
          />
          {visibleParams.size === 0 && (
            <span className="text-xs text-gray-300 italic select-none">Aucun paramètre — cliquez + pour en ajouter</span>
          )}
        </div>
      )}
    </div>
  );
};
